import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { summariseProductEcolabels } from '../../config/ecolabels';

import css from './OrderEcolabelSummary.module.css';

/**
 * "Ecolabels in your order": which ecolabels the products in a basket or order
 * carry, and how many of the products carry each one.
 *
 * It only restates the ecolabels on the products themselves — "UK made: 2 of 3
 * products" — and makes no claim about the order's impact (no CO₂ saved, no
 * overall score), in line with the Green Claims Code.
 *
 * @component
 * @param {Object} props
 * @param {Array<{id: string, ecolabelKeys: Array<string>}>} props.products one entry
 *   per product; the same product bought in two sizes is counted once
 * @param {'basket'|'order'} [props.context='order'] which wording to use
 * @param {string} [props.className]
 * @returns {JSX.Element|null} null when none of the products carry an ecolabel
 */
const OrderEcolabelSummary = props => {
  const { products, context = 'order', className } = props;
  const { productCount, ecolabels } = summariseProductEcolabels(products);

  if (ecolabels.length === 0) {
    return null;
  }

  return (
    <section className={classNames(css.root, className)}>
      <h2 className={css.heading}>
        <FormattedMessage id={`OrderEcolabelSummary.heading.${context}`} />
      </h2>
      <ul className={css.list}>
        {ecolabels.map(({ ecolabel, count }) => (
          <li key={ecolabel.key} className={css.item}>
            <img className={css.badge} src={ecolabel.src} alt="" loading="lazy" />
            <span className={css.label}>{ecolabel.label}</span>
            <span className={css.count}>
              <FormattedMessage
                id={
                  count === productCount
                    ? 'OrderEcolabelSummary.countAll'
                    : 'OrderEcolabelSummary.countSome'
                }
                values={{ count, total: productCount }}
              />
            </span>
          </li>
        ))}
      </ul>
      <p className={css.note}>
        <FormattedMessage id="OrderEcolabelSummary.note" />
      </p>
    </section>
  );
};

export default OrderEcolabelSummary;
